import React, { useEffect, useState, useCallback } from 'react';
import { Archive, Upload, Trash2, Search, Loader2, Image as ImageIcon, FileText, Film, Sparkles, CheckCircle2 } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, PanelLoader, EmptyState, Card, Field, Chip, inputCls } from './shared';
import { toast } from 'sonner';

export default function AssetLibraryPanel({ campaigns = [] }) {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [prompt, setPrompt] = useState('');
  const [kind, setKind] = useState('ad_creative');
  const [campaignId, setCampaignId] = useState('');

  const load = useCallback(() => {
    suiteAPI.assetLibrary({ folder: folder || undefined, q: q || undefined })
      .then(r => { setAssets(r.data.assets); setFolders(r.data.folders); })
      .finally(() => setLoading(false));
  }, [folder, q]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy('upload');
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('folder', folder || 'Uploads');
      if (campaignId) fd.append('campaign_id', campaignId);
      try { await suiteAPI.uploadAsset(fd); } catch { toast.error(`${f.name} failed`); }
    }
    setBusy(''); load(); toast.success('Uploaded');
  };

  const generate = async () => {
    if (!prompt.trim()) return toast.error('Describe the creative you want');
    setBusy('ai');
    try {
      await suiteAPI.aiImage({ prompt, kind, campaign_id: campaignId || null });
      toast.success('Creative generated');
      setPrompt(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setBusy(''); }
  };

  const sendForApproval = async (a) => {
    await suiteAPI.requestApproval({ item_type: 'asset', item_id: a.id, item_name: a.name, preview_url: suiteAPI.fileUrl(a.id) });
    toast.success('Sent for approval');
  };

  return (
    <Panel title="Asset Library" subtitle="Every image, video, design export and AI creative in one searchable place — organised by folder and campaign." testId="suite-assets"
      action={
        <label>
          <Button asChild className="bg-[#b8ff00] text-black font-bold" data-testid="asset-upload-btn">
            <span className="cursor-pointer flex items-center">{busy === 'upload' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />} Upload files</span>
          </Button>
          <input type="file" multiple onChange={upload} className="hidden" />
        </label>
      }>
      <Card className="p-5 mb-5 space-y-3">
        <div className="flex flex-wrap gap-2 items-end">
          <Field label="Generate an AI creative">
            <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={2} placeholder="Bold product hero shot of a black bomber jacket on concrete, neon rim light" className={`${inputCls} w-[380px] max-w-full`} data-testid="asset-ai-prompt" />
          </Field>
          <Field label="Type">
            <select value={kind} onChange={e => setKind(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2" data-testid="asset-ai-kind">
              {['ad_creative', 'background', 'product', 'lifestyle', 'social', 'logo_concept'].map(k => <option key={k} value={k}>{k.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="Campaign">
            <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2">
              <option value="">None</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Button onClick={generate} disabled={busy === 'ai'} className="bg-violet-500 text-white" data-testid="asset-ai-generate">
            {busy === 'ai' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Generate
          </Button>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-zinc-500" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search assets…" className={`${inputCls} pl-8 h-9 w-56`} data-testid="asset-search" />
        </div>
        <Chip active={!folder} onClick={() => setFolder('')} testId="asset-folder-all">All</Chip>
        {folders.map(f => <Chip key={f} active={folder === f} onClick={() => setFolder(f)} testId={`asset-folder-${f}`}>{f}</Chip>)}
      </div>

      {loading ? <PanelLoader /> : assets.length === 0 ? (
        <EmptyState icon={Archive} text="Nothing here yet. Upload files or generate an AI creative." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {assets.map(a => (
            <Card key={a.id} className="overflow-hidden group" data-testid={`asset-${a.id}`}>
              <div className="relative">
                {(a.content_type || '').startsWith('image/') ? (
                  <img src={suiteAPI.fileUrl(a.id)} alt={a.name} className="w-full aspect-square object-cover" />
                ) : (a.content_type || '').startsWith('video/') ? (
                  <div className="w-full aspect-square flex items-center justify-center bg-zinc-950 text-zinc-600"><Film size={22} /></div>
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center bg-zinc-950 text-zinc-600"><FileText size={22} /></div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <a href={suiteAPI.fileUrl(a.id)} target="_blank" rel="noreferrer"><Button size="sm" className="bg-zinc-800 text-white text-xs">Open</Button></a>
                  <Button size="sm" variant="ghost" className="text-white" onClick={() => sendForApproval(a)} data-testid={`asset-approve-${a.id}`}><CheckCircle2 size={13} /></Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => suiteAPI.deleteAsset(a.id).then(load)}><Trash2 size={13} /></Button>
                </div>
              </div>
              <div className="p-2">
                <p className="text-[11px] text-white truncate">{a.name}</p>
                <p className="text-[10px] text-zinc-500">{a.folder} {a.size ? `· ${Math.round(a.size / 1024)}kb` : ''}</p>
              </div>
            </Card>
          ))}
        </div>
      )}
      <p className="text-[11px] text-zinc-600 mt-4 flex items-center gap-1.5"><ImageIcon size={12} /> Asset links work inside emails and landing pages — hover an asset and hit Open to copy its URL.</p>
    </Panel>
  );
}
